import React from "react";
import { Link } from "react-router-dom";
import { Navigate } from "react-router-dom";
import ShopNav from "@/components/ShopNav";
import Footer from "@/components/sections/Footer";
import { useLang } from "@/i18n/LanguageContext";
import { useCart } from "@/context/CartContext";
import { Minus, Plus, X, ArrowRight } from "lucide-react";
import { PRE_HARVEST_MODE } from "@/lib/siteMode";

export default function CartPage() {
  const { t, lang } = useLang();
  const { items, updateQty, removeItem, totals } = useCart();

  if (PRE_HARVEST_MODE) {
    return <Navigate to={lang === "en" ? "/en" : "/"} replace />;
  }

  const euro = (n) => n.toFixed(2).replace(".", ",") + " €";

  if (items.length === 0) {
    return (
      <main data-testid="cart-page" className="min-h-screen bg-cream-100 text-charcoal">
        <ShopNav />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <h1 className="font-serif text-4xl text-olive-900">{t("cart_page.empty_title")}</h1>
          <p className="mt-4 text-olive-800/80 font-light">{t("cart_page.empty_body")}</p>
          <Link
            to="/tienda"
            data-testid="cart-goshop"
            className="mt-8 inline-flex items-center gap-2 px-8 py-4 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800"
          >
            {t("cart_page.go_shop")} <ArrowRight size={14} />
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main data-testid="cart-page" className="min-h-screen bg-cream-100 text-charcoal">
      <ShopNav />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12 sm:py-16">
        <h1 className="font-serif text-4xl sm:text-5xl text-olive-900">{t("cart_page.title")}</h1>

        <div className="mt-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="border-t border-cream-400">
              {items.map((it) => (
                <div
                  key={it.product_id}
                  data-testid={`cart-item-${it.slug}`}
                  className="py-6 border-b border-cream-400 grid grid-cols-[auto_1fr_auto] gap-4 items-center"
                >
                  <Link to={`/producto/${it.slug}`} className="w-20 h-24 bg-cream-200 overflow-hidden block">
                    <img
                      src={it.image}
                      alt={lang === "es" ? it.name_es : it.name_en}
                      className="h-full w-full object-contain p-2"
                    />
                  </Link>
                  <div className="flex flex-col gap-2">
                    <Link
                      to={`/producto/${it.slug}`}
                      className="font-serif text-lg text-olive-900 hover:text-olive-700"
                    >
                      {lang === "es" ? it.name_es : it.name_en}
                    </Link>
                    <div className="text-sm text-olive-800/70 font-light">{euro(it.price)}</div>
                    <div className="mt-1 flex items-center gap-3">
                      <div className="inline-flex items-center border border-olive-700/30">
                        <button
                          onClick={() => updateQty(it.product_id, it.qty - 1)}
                          className="px-2.5 py-2 hover:bg-cream-200"
                          aria-label="minus"
                          data-testid={`cart-minus-${it.slug}`}
                        >
                          <Minus size={12} strokeWidth={1.5} />
                        </button>
                        <span className="px-3 text-sm font-serif text-olive-900 min-w-[24px] text-center">
                          {it.qty}
                        </span>
                        <button
                          onClick={() => updateQty(it.product_id, it.qty + 1)}
                          className="px-2.5 py-2 hover:bg-cream-200"
                          aria-label="plus"
                          data-testid={`cart-plus-${it.slug}`}
                        >
                          <Plus size={12} strokeWidth={1.5} />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(it.product_id)}
                        data-testid={`cart-remove-${it.slug}`}
                        className="text-xs text-olive-800/60 hover:text-red-700 inline-flex items-center gap-1"
                      >
                        <X size={12} /> {t("cart_page.remove")}
                      </button>
                    </div>
                  </div>
                  <div className="font-serif text-olive-900 text-lg text-right">
                    {euro(it.price * it.qty)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="bg-cream-200 border border-cream-400 p-6 sm:p-8 sticky top-24">
              <h2 className="font-serif text-2xl text-olive-900">{t("cart_page.summary")}</h2>
              <dl className="mt-6 space-y-3 text-sm">
                <div className="flex justify-between text-olive-800/85 font-light">
                  <dt>{t("cart_page.subtotal")}</dt>
                  <dd className="font-serif text-olive-900 text-base">{euro(totals.subtotal)}</dd>
                </div>
                <div className="flex justify-between text-olive-800/85 font-light">
                  <dt>{t("cart_page.shipping")}</dt>
                  <dd className="text-olive-800/60 text-xs">{t("cart_page.shipping_calculated")}</dd>
                </div>
              </dl>
              <Link
                to="/checkout"
                data-testid="cart-checkout"
                className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800"
              >
                {t("cart_page.checkout")} <ArrowRight size={14} />
              </Link>
              <Link
                to="/tienda"
                className="mt-3 block text-center text-[11px] uppercase tracking-[0.24em] text-olive-800/70 hover:text-olive-900"
              >
                {t("cart_page.continue_shopping")}
              </Link>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </main>
  );
}
